/**
 * DataTable — Tabla densa con search, sort, filter, paginación, bulk select.
 * Diseñada para gestión de catálogos B2B (estilo Linear / Shopify Admin).
 *
 * Props:
 *   columns        [{ key, label, render?, sortable?, width?, align?, sortValue?(row) }]
 *   rows           array
 *   loading        bool — muestra skeleton
 *   rowKey         string (default 'id')
 *   search         { value, onChange, placeholder } — input de búsqueda
 *   filters        [{ key, label, value, options:[{value,label}], onChange }] — chips
 *   bulkActions    [{ label, color?, icon?, onClick(ids), confirm? }] — barra inferior
 *   pageSize       number (default 25)
 *   onRowClick     (row) => void — abre slide-over
 *   emptyState     ReactNode — qué mostrar si no hay rows
 *   actions        ReactNode — botones a la derecha del header (ej. "Nuevo")
 *   stickyHeader   bool (default true)
 */
import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, TextField, InputAdornment, IconButton,
  Checkbox, Pagination, MenuItem, Select, FormControl, InputLabel,
  Chip, Skeleton, Tooltip, Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import CloseIcon from '@mui/icons-material/Close';

const HEADER_BG = '#f8fafc';
const ROW_HOVER = '#f0f9ff';

export default function DataTable({
  columns,
  rows = [],
  loading = false,
  rowKey = 'id',
  search,
  filters = [],
  bulkActions = [],
  pageSize = 25,
  onRowClick,
  emptyState,
  actions,
  stickyHeader = true,
}) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');  // 'asc' | 'desc'
  const [page, setPage] = useState(1);
  const [pageSizeState, setPageSizeState] = useState(pageSize);
  const [selected, setSelected] = useState(new Set());

  // Sorting
  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    const col = columns.find(c => c.key === sortKey);
    if (!col) return rows;
    const getValue = col.sortValue || (r => r[sortKey]);
    return [...rows].sort((a, b) => {
      const va = getValue(a);
      const vb = getValue(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return sortDir === 'asc' ? va - vb : vb - va;
      const sa = String(va).toLowerCase();
      const sb = String(vb).toLowerCase();
      return sortDir === 'asc' ? sa.localeCompare(sb) : sb.localeCompare(sa);
    });
  }, [rows, sortKey, sortDir, columns]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSizeState));
  const paginatedRows = useMemo(() => {
    const start = (page - 1) * pageSizeState;
    return sortedRows.slice(start, start + pageSizeState);
  }, [sortedRows, page, pageSizeState]);

  const allSelected = paginatedRows.length > 0 && paginatedRows.every(r => selected.has(r[rowKey]));
  const someSelected = paginatedRows.some(r => selected.has(r[rowKey])) && !allSelected;

  const toggleAll = () => {
    const newSel = new Set(selected);
    if (allSelected) {
      paginatedRows.forEach(r => newSel.delete(r[rowKey]));
    } else {
      paginatedRows.forEach(r => newSel.add(r[rowKey]));
    }
    setSelected(newSel);
  };

  const toggleOne = (id) => {
    const newSel = new Set(selected);
    if (newSel.has(id)) newSel.delete(id);
    else newSel.add(id);
    setSelected(newSel);
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const showBulkBar = bulkActions.length > 0 && selected.size > 0;
  const activeFilters = filters.filter(f => f.value != null && f.value !== '' && f.value !== 'todos');

  return (
    <Box>
      {/* Header: search + filters + actions */}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        {search && (
          <TextField
            size="small"
            value={search.value}
            onChange={e => { search.onChange(e.target.value); setPage(1); }}
            placeholder={search.placeholder || 'Buscar...'}
            sx={{ minWidth: 260, '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: 2 } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
              endAdornment: search.value ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => { search.onChange(''); setPage(1); }}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        )}
        {filters.map(f => (
          <FormControl key={f.key} size="small" sx={{ minWidth: 160, bgcolor: '#fff', borderRadius: 2 }}>
            <InputLabel>{f.label}</InputLabel>
            <Select label={f.label} value={f.value || 'todos'}
              onChange={e => { f.onChange(e.target.value === 'todos' ? '' : e.target.value); setPage(1); }}>
              <MenuItem value="todos">Todos</MenuItem>
              {(f.options || []).map(o => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        ))}
        <Box sx={{ flex: 1 }} />
        {actions}
      </Box>

      {/* Active filter chips */}
      {activeFilters.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
          {activeFilters.map(f => {
            const optLabel = (f.options || []).find(o => o.value === f.value)?.label || f.value;
            return (
              <Chip key={f.key} size="small" label={`${f.label}: ${optLabel}`}
                onDelete={() => f.onChange('')}
                sx={{ bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 600, fontSize: 11 }} />
            );
          })}
        </Box>
      )}

      {/* Bulk actions bar */}
      {showBulkBar && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, mb: 1.5,
          p: 1, px: 1.5, borderRadius: 2, bgcolor: '#0d9488', color: '#fff',
        }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, flex: 1 }}>
            {selected.size} seleccionado{selected.size !== 1 ? 's' : ''}
          </Typography>
          {bulkActions.map((b, i) => (
            <Button key={i} size="small" startIcon={b.icon}
              onClick={() => b.onClick(Array.from(selected), () => setSelected(new Set()))}
              sx={{ color: '#fff', textTransform: 'none', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
              {b.label}
            </Button>
          ))}
          <IconButton size="small" onClick={() => setSelected(new Set())} sx={{ color: '#fff' }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      )}

      {/* Tabla */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
        <Table stickyHeader={stickyHeader} size="small">
          <TableHead>
            <TableRow>
              {bulkActions.length > 0 && (
                <TableCell padding="checkbox" sx={{ bgcolor: HEADER_BG, borderBottom: '1px solid #e2e8f0' }}>
                  <Checkbox size="small" checked={allSelected} indeterminate={someSelected} onChange={toggleAll} />
                </TableCell>
              )}
              {columns.map(col => (
                <TableCell key={col.key} align={col.align || 'left'}
                  sx={{ bgcolor: HEADER_BG, fontWeight: 700, fontSize: 11, textTransform: 'uppercase',
                        letterSpacing: 0.5, color: '#475569', whiteSpace: 'nowrap',
                        width: col.width, borderBottom: '1px solid #e2e8f0',
                        cursor: col.sortable ? 'pointer' : 'default',
                        '&:hover': col.sortable ? { color: '#0d9488' } : {} }}
                  onClick={col.sortable ? () => handleSort(col.key) : undefined}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: col.align === 'right' ? 'flex-end' : col.align === 'center' ? 'center' : 'flex-start' }}>
                    {col.label}
                    {col.sortable && (
                      sortKey === col.key
                        ? (sortDir === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 13 }} /> : <ArrowDownwardIcon sx={{ fontSize: 13 }} />)
                        : <UnfoldMoreIcon sx={{ fontSize: 13, opacity: 0.3 }} />
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {bulkActions.length > 0 && <TableCell><Skeleton width={20} /></TableCell>}
                  {columns.map(c => <TableCell key={c.key}><Skeleton /></TableCell>)}
                </TableRow>
              ))
            ) : paginatedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + (bulkActions.length > 0 ? 1 : 0)} sx={{ py: 6, border: 'none' }}>
                  {emptyState || (
                    <Typography align="center" sx={{ color: '#94a3b8', fontSize: 13 }}>
                      Sin resultados
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              paginatedRows.map(row => {
                const id = row[rowKey];
                const isSelected = selected.has(id);
                return (
                  <TableRow key={id} hover
                    onClick={(e) => {
                      // Si click viene de la celda de checkbox, no abrir
                      if (e.target.closest('input[type="checkbox"]')) return;
                      onRowClick?.(row);
                    }}
                    sx={{
                      cursor: onRowClick ? 'pointer' : 'default',
                      bgcolor: isSelected ? '#f0fdf4' : 'transparent',
                      '&:hover': { bgcolor: isSelected ? '#dcfce7' : ROW_HOVER },
                    }}>
                    {bulkActions.length > 0 && (
                      <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                        <Checkbox size="small" checked={isSelected} onChange={() => toggleOne(id)} />
                      </TableCell>
                    )}
                    {columns.map(col => (
                      <TableCell key={col.key} align={col.align || 'left'}
                        sx={{ fontSize: 12, color: '#0f172a', borderBottom: '1px solid #f1f5f9' }}>
                        {col.render ? col.render(row) : row[col.key]}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer: paginación */}
      {sortedRows.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontSize: 12, color: '#64748b' }}>
            Mostrando {(page - 1) * pageSizeState + 1}–{Math.min(page * pageSizeState, sortedRows.length)} de {sortedRows.length}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <Select value={pageSizeState} onChange={e => { setPageSizeState(e.target.value); setPage(1); }}
                sx={{ fontSize: 12 }}>
                {[10, 25, 50, 100].map(n => <MenuItem key={n} value={n} sx={{ fontSize: 12 }}>{n} / pág</MenuItem>)}
              </Select>
            </FormControl>
            <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} size="small" />
          </Box>
        </Box>
      )}
    </Box>
  );
}
